import { useAtom } from "@dbeining/react-atom";
import { faPlus, faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import type { ISiteOption } from "../../customTypings/haulier/Haulier";
import type { ILocationListRow } from "../../customTypings/location/Location";
import { locationsService } from "../../services/locations/locations.service";
import { sitesService } from "../../services/sites/sites.service";
import {
    addEntityActionLabel,
    buildRecordLifecycleConfirmProps,
    EntityLabels,
    FilterMigration,
    formatPostalAddress,
    getUserFacingErrorMessage,
    ListPageTitles,
    logErrorForDev,
    recordLifecycleListConfirmBodies,
    SectionLabels,
    showToastError,
    showToastSuccess,
} from "../../utils";
import {
    buildLocationAdminPagedListActions,
    canMutateLocations,
    getLocationAdminActionSuccessMessage,
    type LocationAdminConfirmKind,
    type LocationAdminListRow,
} from "./locationAdminListActions";
type ConfirmKind = LocationAdminConfirmKind | null;

const locationsListFilterMigrations: Record<string, FilterMigration> = {
    hasCoordinates: {
        member: "coordinatesSearch",
        mapValue: () => null,
    },
    postcodeNormalizedPrefix: {
        member: "addressSearch",
        mapValue: (value) => (typeof value === "string" && value.trim() ? value : null),
    },
};

function formatLocationAddressSummary(item: {
    addressLine1?: string | null;
    town?: string | null;
    county?: string | null;
    postcode?: string | null;
}): string {
    return formatPostalAddress({
        addressLine1: item.addressLine1,
        town: item.town,
        county: item.county,
        postcode: item.postcode,
    });
}

function formatLocationCoordinates(item: {
    latitude?: number | null;
    longitude?: number | null;
    hasCoordinates?: boolean;
}): string {
    if (!item.hasCoordinates || item.latitude == null || item.longitude == null) {
        return "-";
    }
    return `${item.latitude.toFixed(6)}, ${item.longitude.toFixed(6)}`;
}

function renderAssociatedCustomers(item: {
    primaryCustomerDisplay?: string | null;
    primaryCustomerMainLocationFlags?: string | null;
    associatedCustomerCount?: number;
}): ReactNode {
    const names = item.primaryCustomerDisplay?.trim();
    if (!names) return "";
    const count = item.associatedCustomerCount ?? 0;
    const nameParts = names.split(", ");
    const mainLocationFlags = item.primaryCustomerMainLocationFlags?.split(",") ?? [];

    return (
        <>
            {nameParts.map((name, index) => (
                <span key={`${name}-${index}`}>
                    {index > 0 && ", "}
                    {mainLocationFlags[index] === "1" && (
                        <FontAwesomeIcon
                            icon={faStar}
                            className="text-warning me-1"
                            title="Primary address"
                            aria-label="Primary address"
                            fixedWidth
                        />
                    )}
                    {name}
                </span>
            ))}
            {count > 3 && "..."}
        </>
    );
}

const LocationsListPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateLocations(roleNames);

    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [sitesError, setSitesError] = useState<string | null>(null);

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmLocationId, setConfirmLocationId] = useState<string | null>(null);
    const [confirmLocationLabel, setConfirmLocationLabel] = useState("");

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                setSitesError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("LocationsListPage.sites", e);
                setSitesError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setSitesLoading(false));
    }, []);

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmLocationId(null);
        setConfirmLocationLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmLocationId || !confirmKind) return;
        const id = confirmLocationId;
        const op =
            confirmKind === "disable"
                ? locationsService.disable(id)
                : confirmKind === "enable"
                  ? locationsService.enable(id)
                  : confirmKind === "softDelete"
                    ? locationsService.softDelete(id)
                    : locationsService.restore(id);
        op.then(() => {
            showToastSuccess(getLocationAdminActionSuccessMessage(confirmKind, confirmLocationLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            showToastError(getUserFacingErrorMessage(e, "That action couldn't be completed. Please try again."));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: LocationAdminListRow) => {
        setConfirmKind(kind);
        setConfirmLocationId(item.id);
        setConfirmLocationLabel(item.name);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.location,
                confirmLocationLabel,
                recordLifecycleListConfirmBodies(confirmLocationLabel)
            ),
        [confirmKind, confirmLocationLabel]
    );

    const siteFilterOptions = useMemo(() => {
        const opts: Record<string, string | null> = { "All sites": null };
        for (const s of sites) {
            opts[s.siteName] = s.id;
        }
        return opts;
    }, [sites]);

    const columns: IColumn[] = useMemo(
        () => [
            { member: "name", renderAs: ColumnRender.text, title: "Name", width: 180 } as IColumn,
            {
                member: "address",
                renderAs: ColumnRender.customRenderer,
                title: "Address",
                width: 260,
                customRenderer: (item: {
                    addressLine1?: string | null;
                    town?: string | null;
                    county?: string | null;
                    postcode?: string | null;
                }) => (
                    <td className="text-start text-muted small">{formatLocationAddressSummary(item) || "-"}</td>
                ),
            } as IColumn,
            {
                member: "latitude",
                renderAs: ColumnRender.customRenderer,
                title: "Map position",
                width: 180,
                customRenderer: (item: ILocationListRow) => (
                    <td className="text-start font-monospace small">{formatLocationCoordinates(item)}</td>
                ),
            } as IColumn,
            {
                member: "primaryCustomerDisplay",
                renderAs: ColumnRender.customRenderer,
                title: "Customers",
                width: 220,
                customRenderer: (item: ILocationListRow) => (
                    <td className="text-start">{renderAssociatedCustomers(item)}</td>
                ),
            } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(() => {
        const list: IFilter[] = [];
        if (sites.length > 1) {
            list.push({
                title: "Associated site",
                member: "associatedSiteId",
                type: FilterType.select,
                options: siteFilterOptions,
                defaultValue: null,
            } as IFilter);
        }
        list.push(
            { title: "Name", member: "name", type: FilterType.text } as IFilter,
            { title: "Address", member: "addressSearch", type: FilterType.text } as IFilter,
            { title: "Map position", member: "coordinatesSearch", type: FilterType.text } as IFilter,
            { title: "Customers", member: "associatedCustomerSearch", type: FilterType.text } as IFilter,
            {
                title: "Enabled",
                member: "isEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter
        );
        return list;
    }, [sites.length, siteFilterOptions]);

    const itemActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: "Edit",
                onClick: (item: LocationAdminListRow) => navigate(`/locations/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildLocationAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [canMutate, navigate]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: addEntityActionLabel(EntityLabels.location),
                icon: faPlus,
                onClick: () => navigate("/locations/new"),
                primary: true,
            } as IPagedListAction,
        ];
    }, [canMutate, navigate]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.locations}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: SectionLabels.operations, to: "/operations" },
                            { text: ListPageTitles.locations },
                        ]}
                    />
                    <h1>{ListPageTitles.locations}</h1>
                    <p className="text-muted">
                        Locations used across your organisation, with map positions. Locations can be linked to
                        customers across sites you can access.
                    </p>
                    {sitesError && <Alert variant="danger">{sitesError}</Alert>}
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    {sitesLoading ? (
                        <Alert variant="info">Loading sites…</Alert>
                    ) : sites.length === 0 ? (
                        <Alert variant="warning">No sites are available for your account.</Alert>
                    ) : (
                        <PagedList
                            columns={columns}
                            filters={filters}
                            headActions={headActions}
                            itemActions={itemActions}
                            rowActionsAriaLabel="Location actions"
                            showDeletedRowIndicator
                            defaultSortBy="Name"
                            defaultSortOrder={SortOrder.ascending}
                            getUrl="/api/locations/all"
                            rowFormatter={() => null}
                            persistanceKey="LocationsListPage"
                            filterMigrations={locationsListFilterMigrations}
                            refreshNumber={refreshNumber}
                        />
                    )}
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { LocationsListPage };
